import { z } from "zod";
// ---------- Supplier (procurement) ----------
export const supplierFulfilmentMode = z.enum(["depot", "dropship"]);
/** Informational contact row stored in suppliers.contacts_json. */
export const SupplierContact = z.object({
    name: z.string().min(1).max(120),
    role: z.string().max(80).nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    email: z.email().max(254).nullable().optional(),
});
export const SupplierAddress = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).nullable().optional(),
    city: z.string().min(1).max(100),
    postcode: z.string().min(1).max(20),
    country: z.string().length(2).default("GB"),
});
export const CreateSupplierBody = z.object({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be kebab-case"),
    name: z.string().min(1).max(200),
    /** Deterministic PO recipient address. */
    email: z.email().max(254),
    phone: z.string().max(40).nullable().optional(),
    contacts: z.array(SupplierContact).max(20).default([]),
    address_json: SupplierAddress.nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    active: z.boolean().default(true),
    fulfilment_mode: supplierFulfilmentMode.default("depot"),
});
// Explicit partial object — mirrors UpdateBrandBody pattern.
// Using CreateSupplierBody.partial() would re-inject .default() values (e.g.
// contacts=[], active=true) even on a sparse PATCH, violating the "partial
// update must not fabricate fields" contract tested by the suite.
export const UpdateSupplierBody = z.object({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be kebab-case")
        .optional(),
    name: z.string().min(1).max(200).optional(),
    email: z.email().max(254).optional(),
    phone: z.string().max(40).nullable().optional(),
    contacts: z.array(SupplierContact).max(20).optional(),
    address_json: SupplierAddress.nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
    active: z.boolean().optional(),
    fulfilment_mode: supplierFulfilmentMode.optional(),
});
// ---------- Supplier portal users (P2) ----------
export const CreateSupplierUserBody = z.object({
    email: z.email().max(254),
    /** Initial password set by the admin (same model as staff invites). */
    password: z.string().min(8).max(128),
    name: z.string().min(1).max(120).optional(),
});
export const UpdatePortalPoStatusBody = z
    .object({
    status: z.enum(["acknowledged", "preparing", "ready", "shipped"]),
    tracking_note: z.string().max(200).optional(),
})
    .refine((b) => b.status === "shipped" || b.tracking_note == null, {
    message: "tracking_note is only allowed when status is shipped",
    path: ["tracking_note"],
});
export const ReceivePoBody = z
    .object({
    with_issues: z.boolean(),
    note: z.string().max(2000).optional(),
})
    .refine((b) => !b.with_issues || (b.note != null && b.note.trim().length > 0), {
    message: "note is required when with_issues is true",
    path: ["note"],
});
export const UpdateSupplierOrderBody = z.object({
    needed_by: z.string().date().nullable(),
});
export const ListPortalPosQuery = z.object({
    status: z
        .enum(["new", "acknowledged", "preparing", "ready", "received", "shipped", "cancelled", "closed"])
        .optional(),
});
